import React, { useState, useEffect, useMemo } from 'react';
import { DonationService } from '../../services/DonationService';
import { ProjectService } from '../../services/ProjectService';
import { useOrganization } from '../../contexts/OrganizationContext';
import * as WechatAuthService from '../../services/WechatAuthService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { Donation } from '../../shared/models/Donation';
import { Project } from '../../shared/models/Project';
import { TopBar } from '../../components/TopBar/TopBar';
import { Pagination } from '../../components/Pagination/Pagination';
import './MyProjectsView.css';

interface MyProjectsViewProps {
  onBack: () => void;
  onProjectClick?: (projectId: string) => void;
  onDonationRecordClick?: (projectId: string) => void;
}

interface ProjectDonationStats {
  project: Project;
  donationCount: number;
  totalAmount: number;
}

/**
 * 我参与的项目列表页面
 */
export const MyProjectsView: React.FC<MyProjectsViewProps> = ({
  onBack,
  onProjectClick,
  onDonationRecordClick,
}) => {
  const { organization } = useOrganization();
  const [projects, setProjects] = useState<ProjectDonationStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        showError('未获取到微信授权信息', 'MyProjectsView.loadProjects');
        setLoading(false);
        return;
      }

      // 获取所有已支付的捐赠记录
      const allDonations: Donation[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params: any = {
          donorOpenId: openId,
          page: currentPage,
          limit: 100,
          status: 'PAID',
        };
        // 根据机构类型决定使用哪个过滤参数
        if (organization?.type === 'PUBLIC') {
          params.organizationId = organization.id;
        } else if (organization?.type === 'EXECUTING') {
          params.executingOrganizationId = organization.id;
        }
        const response = await DonationService.getDonations(params);

        // 如果当前页没有数据，说明已经到最后一页了
        if (response.list.length === 0) {
          hasMore = false;
          break;
        }

        allDonations.push(...response.list.filter(d => d.status === 'PAID'));
        
        // 判断是否还有下一页：当前页小于总页数，且当前页返回的数据量等于 limit（说明可能还有下一页）
        const limit = 100; // 每页数量
        hasMore = currentPage < response.totalPages && response.list.length === limit;
        currentPage++;

        // 防止无限循环
        if (currentPage > 100) {
          break;
        }
      }

      // 按项目ID分组统计
      const projectStatsMap = new Map<string, { donations: Donation[] }>();
      allDonations.forEach(donation => {
        if (!donation.projectId) return;
        
        if (!projectStatsMap.has(donation.projectId)) {
          projectStatsMap.set(donation.projectId, { donations: [] });
        }
        projectStatsMap.get(donation.projectId)!.donations.push(donation);
      });

      // 获取项目详情并计算统计信息
      const projectIds = Array.from(projectStatsMap.keys());
      const projectPromises = projectIds.map(id => ProjectService.getProjectById(id));
      const projectList = await Promise.all(projectPromises);

      const projectStats: ProjectDonationStats[] = [];
      projectList.forEach(project => {
        if (!project) return;
        
        const stats = projectStatsMap.get(project.id);
        if (!stats) return;

        let totalAmount = 0;
        stats.donations.forEach(donation => {
          if (donation.donationType === 'MONTHLY' && donation.totalDonationAmount) {
            totalAmount += donation.totalDonationAmount;
          } else {
            totalAmount += donation.amount;
          }
        });

        projectStats.push({
          project,
          donationCount: stats.donations.length,
          totalAmount,
        });
      });

      // 按总金额降序排序
      projectStats.sort((a, b) => b.totalAmount - a.totalAmount);

      setTotal(projectStats.length);

      // 分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setProjects(projectStats.slice(startIndex, endIndex));
    } catch (error) {
      logError('[MyProjectsView] Failed to load projects', error);
      showError('加载项目列表失败', 'MyProjectsView.loadProjects');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProjectClick = (projectId: string) => {
    if (onProjectClick) {
      onProjectClick(projectId);
    }
  };

  const handleDonationRecordClick = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    if (onDonationRecordClick) {
      onDonationRecordClick(projectId);
    }
  };

  if (loading && projects.length === 0) {
    return (
      <div className="my-projects-view">
        <TopBar title="我参与的项目" showBack onBack={onBack} />
        <div className="my-projects-loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="my-projects-view">
      <TopBar title="我参与的项目" showBack onBack={onBack} />
      
      <div className="my-projects-content">
        {projects.length === 0 ? (
          <div className="my-projects-empty">暂无参与的项目</div>
        ) : (
          <>
            <div className="my-projects-list">
              {projects.map((item) => {
                const project = item.project;
                const imageUrl = project.coverThumbUrl || project.coverImageUrl || '/placeholder.svg';

                return (
                  <div
                    key={project.id}
                    className="my-projects-item"
                    onClick={() => handleProjectClick(project.id)}
                  >
                    <div className="my-projects-item-image">
                      <img
                        src={imageUrl}
                        alt={project.title}
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                    <div className="my-projects-item-content">
                      <div className="my-projects-item-title">{project.title}</div>
                      <div className="my-projects-item-stats">
                        已捐赠 {item.donationCount}次 / {item.totalAmount.toFixed(2)} 元
                      </div>
                    </div>
                    <button
                      className="my-projects-item-btn"
                      onClick={(e) => handleDonationRecordClick(project.id, e)}
                    >
                      捐赠记录
                    </button>
                  </div>
                );
              })}
            </div>

            {loading && (
              <div className="my-projects-loading-more">加载中...</div>
            )}

            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

